import {
  Maybe,
  SanityAnnualReview,
  SanityAnnualReviews,
  SanityAttachment,
  SanityPhoto,
  SanityReport,
} from '../../types/generated/graphql-types'
import { Review } from '../components/AnnualReviewsBox'
import { AnnualReviewsProps } from '../components/blocks/AnnualReviews'
import { AnnualReviewsNewDesignProps } from '../components/blocks/AnnualReviewsNewDesign'
import { ImageProps } from '../components/Image'
import { LatestReportsFlashcardProps } from '../components/LatestReportsFlashcard'
import { Attachment, Variant } from '../components/ReportAttachmentsList'
import { ReportsHeroProps } from '../components/ReportsHero'
import { rewriteDisclaimerFileUrl } from '../utilities/attachment'
import { Breakpoints } from '../utilities/breakpoints'
import { notEmpty } from '../utilities/filters'
import { validImage } from '../utilities/imageFilters'
import { Presets } from '../utilities/imagePreset'
import { getImagePropsWithSources } from '../utilities/imagePropUtils'
import { getUrlFragments, SanityNode } from '../utilities/routing'
import { isNullOrWhiteSpace } from '../utilities/string'
import { mapSanityLinkToInternalExternalLink } from './LinkMapper'

export const mapSanityPhotoToImageProps = (photo: SanityPhoto): ImageProps | undefined =>
  validImage(photo)
    ? getImagePropsWithSources(
        photo,
        {
          small: Presets.reportsHero.small,
          medium: Presets.reportsHero.medium,
          large: Presets.reportsHero.large,
          extraLarge: Presets.reportsHero.extraLarge,
        },
        Breakpoints.sm,
        Breakpoints.xl,
        Breakpoints.xxl
      )
    : undefined

export const mapSanityReportToLatestReport = (
  sanityReport: SanityReport,
  withImage = true
): LatestReportsFlashcardProps => ({
  attachments: sanityReport?.attachments?.length
    ? sanityReport.attachments
        .filter(notEmpty)
        .filter(filterSanityEmptyAttachments)
        .map(attachment => mapSanityAttachmentToAttachment(attachment))
    : undefined,
  imageProps:
    withImage && sanityReport?.heroImage?.asset
      ? mapSanityPhotoToImageProps(sanityReport?.heroImage)
      : undefined,
  path: sanityReport?.button?.pageReference?.page?.path ?? '',
  publishDate: sanityReport?.publishDate ?? '',
  title: sanityReport?.title ?? '',
})

export const mapSanityReportToReportHero = (
  sanityReport: SanityReport
): ReportsHeroProps => ({
  title: sanityReport?.title ?? '',
  publishDate: sanityReport?.publishDate ?? '',
  heading: sanityReport?.heading ?? '',
  imageProps: sanityReport?.heroImage?.asset
    ? mapSanityPhotoToImageProps(sanityReport?.heroImage)
    : undefined,
  videoProps: sanityReport?.beigeBackgroundVideo?.url
    ? {
        previewImage: sanityReport.beigeBackgroundVideo.image
          ? mapSanityPhotoToImageProps(sanityReport.beigeBackgroundVideo.image)
          : undefined,
        title: sanityReport.beigeBackgroundVideo.title ?? '',
        videoUrl: sanityReport.beigeBackgroundVideo.url ?? '',
      }
    : sanityReport?.backgroundVideo?.url
    ? {
        previewImage: sanityReport.backgroundVideo.image
          ? mapSanityPhotoToImageProps(sanityReport.backgroundVideo.image)
          : undefined,
        title: sanityReport.backgroundVideo.title ?? '',
        videoUrl: sanityReport.backgroundVideo.url ?? '',
      }
    : undefined,
  preamble: sanityReport?.preamble ?? '',
  button: sanityReport?.button
    ? mapSanityLinkToInternalExternalLink(sanityReport?.button)
    : undefined,
  attachments: sanityReport?.attachments?.length
    ? sanityReport.attachments
        .filter(notEmpty)
        .filter(filterSanityEmptyAttachments)
        .map(attachment => mapSanityAttachmentToAttachment(attachment))
    : undefined,
})

export const mapSanityAnnualReviewToReview = (
  sanityAnnualReview: SanityAnnualReview
): Review => ({
  attachments: sanityAnnualReview?.attachments?.length
    ? sanityAnnualReview.attachments
        .filter(notEmpty)
        .filter(filterSanityEmptyAttachments)
        .map(attachment => mapSanityAttachmentToAttachment(attachment))
    : undefined,
  title: sanityAnnualReview?.title ?? '',
})

export const mapSanityAnnualReviewsToAnnualReviewsProps = (
  sanityAnnualReviews: SanityAnnualReviews
): AnnualReviewsProps => {
  const reviews = sanityAnnualReviews?.annualReviews?.length
    ? sanityAnnualReviews.annualReviews
        .filter(notEmpty)
        .map(mapSanityAnnualReviewToReview)
    : []
  return {
    title: sanityAnnualReviews?.title ?? '',
    reviewsList: reviews,
  }
}

export const mapSanityAnnualReviewsToAnnualReviewsNewDesignProps = ({
  annualReviews,
  title,
}: SanityAnnualReviews): AnnualReviewsNewDesignProps => ({
  title: title ?? '',
  reviews:
    annualReviews?.filter(notEmpty).map(mapSanityAnnualReviewToReview) ?? undefined,
})

export const filterSanityEmptyAttachments = (
  sanityAttachment: SanityAttachment
): boolean => !!(sanityAttachment?.externalUrl || sanityAttachment?.file?.asset?.url)

export const mapSanityAttachmentToAttachment = (
  sanityAttachment: SanityAttachment,
  siteWideDisclaimerEnabledForThisPage = false
): Attachment => {
  let url: string | null = null

  if (!isNullOrWhiteSpace(sanityAttachment.externalUrl)) {
    url = sanityAttachment.externalUrl
  } else {
    const asset = sanityAttachment.file?.asset

    if (asset?.url) {
      if (sanityAttachment.disclaimerPage || siteWideDisclaimerEnabledForThisPage) {
        if (sanityAttachment.disclaimerPage) {
          let disclaimerPath = sanityAttachment.disclaimerPage.path

          // Fallback to getUrlFragments() if path is missing, since it may not exist for on demand press releases.
          if (isNullOrWhiteSpace(disclaimerPath)) {
            const disclaimerUrlFragments = getUrlFragments(
              sanityAttachment.disclaimerPage as SanityNode
            )
            disclaimerPath = `/${disclaimerUrlFragments.join('/')}`
          }

          url = rewriteDisclaimerFileUrl(asset, disclaimerPath)
        } else {
          url = rewriteDisclaimerFileUrl(asset)
        }

        if (url) {
          url = `${url}&dl`
        }
      } else {
        url = `${asset.url}?dl=${asset.originalFilename}`
      }
    }
  }

  return {
    date: sanityAttachment.publishDate ?? '',
    title: sanityAttachment.title ?? sanityAttachment.file?.asset?.originalFilename ?? '',
    type: (sanityAttachment.type ?? 'file') as Variant,
    url: url ?? '',
  }
}

export const mapSanityAttachmentArrayToSingleAttachment = (
  sanityAttachmentArray: Maybe<Maybe<SanityAttachment>[]> | undefined
): Attachment | undefined =>
  mapSanityAttachmentArrayToAttachmentArray(sanityAttachmentArray)[0]

export const mapSanityAttachmentArrayToAttachmentArray = (
  sanityAttachmentArray: Maybe<Maybe<SanityAttachment>[]> | undefined
): Attachment[] =>
  sanityAttachmentArray
    ?.filter(notEmpty)
    .filter(filterSanityEmptyAttachments)
    .map(attachment => mapSanityAttachmentToAttachment(attachment)) ?? []
